import { cn } from "@/lib/utils";
import { Card } from "@/types/poker";

/**
 * The props for PokerCard now accept a `Card` object, which directly
 * matches the data structure sent from the Python backend.
 */
interface PokerCardProps {
  card?: Card; // The card object (e.g., { rank: 'A', suit: { symbol: '♠', ... } })
  hidden?: boolean; // If true, shows the back of the card
  compact?: boolean; // If true, renders a smaller version of the card
}

// Maps the suit symbol to its corresponding Tailwind CSS color class.
const SUIT_COLORS: Record<string, string> = {
  "♥": "text-rose-500",
  "♦": "text-rose-500",
  "♠": "text-slate-900",
  "♣": "text-slate-900",
};

// Maps rank abbreviations to full names for accessibility.
const RANK_NAMES: Record<string, string> = {
  A: "Ace", K: "King", Q: "Queen", J: "Jack", T: "10",
  "2": "2", "3": "3", "4": "4", "5": "5", "6": "6", "7": "7", "8": "8", "9": "9",
};

/**
 * A component that renders a single playing card.
 * It can display either the face of the card (rank and suit) or its back.
 */
export const PokerCard = ({ card, hidden, compact }: PokerCardProps) => {
  // Determine the content to display inside the card.
  // If the card is hidden or doesn't exist, show the card back symbol '🂠'.
  // Otherwise, combine the rank and suit symbol (e.g., "A♠").
  const displayRank = card?.rank === "T" ? "10" : card?.rank;
  const content = hidden || !card ? "🂠" : `${displayRank}${card.suit.symbol}`;

  // Determine the color of the text based on the suit symbol.
  // If the card is hidden, the color will be white.
  const suitColor = !hidden && card?.suit?.symbol ? SUIT_COLORS[card.suit.symbol] : "text-white";

  // Build an accessible label for screen readers.
  const ariaLabel = hidden || !card
    ? "Card back"
    : `${RANK_NAMES[card.rank] ?? card.rank} of ${card.suit.name}`;

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={cn(
        "flex items-center justify-center rounded-sm border shadow-card transition-transform",
        // Apply different sizes based on the 'compact' prop
        compact ? "h-12 w-8 text-xs" : "h-20 w-14 text-lg",
        // Apply different styles for the card's front vs. its back
        hidden || !card
          ? "bg-gradient-to-br from-primary/70 to-primary text-white border-white/20"
          : "bg-gradient-to-br from-white to-slate-100 border-slate-200",
      )}
    >
      <span className={cn("font-semibold", suitColor)}>{content}</span>
    </div>
  );
};
